// src/components/sections/KnowledgeBaseSection.js

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, 
  FileText, Check, AlertCircle, Power,
  X
} from 'lucide-react';
import { colors } from '../../styles/designSystem';
import { GradientButton } from '../ui/GradientButton';
import { Container } from '../ui/Container';
import {
  getKnowledgeBaseDetails,
  createKnowledgeBase,
  updateKnowledgeBase,
  deleteKnowledgeBase,
  activateKnowledgeBase,
  getBusinessProfile,
  getSectorTemplates
} from '../../api';
import { useTranslation } from 'react-i18next';

const KnowledgeBaseCard = ({ kb, onEdit, onDelete, onActivate }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-grow">
          <h3 className="text-lg font-medium">{kb.name}</h3>
          <div className={`mt-2 text-sm ${kb.isActive ? 'text-green-600' : 'text-gray-500'}`}>
            {kb.isActive ? t('KnowledgeBaseSection.knowledge_base_card.status_active') : t('KnowledgeBaseSection.knowledge_base_card.status_inactive')}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onActivate(kb)}
            className={`p-1 ${kb.isActive ? 'text-green-600' : 'text-gray-400'} hover:text-green-600 transition-colors`}
          >
            <Power className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(kb)}
            className="p-1 text-gray-400 hover:text-violet-600 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(kb)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-gray-600 text-sm line-clamp-3 mb-4">
        {kb.text.substring(0, 200)}...
      </p>
      <div className="text-sm text-gray-500">
        {t('KnowledgeBaseSection.knowledge_base_card.last_updated')}: {new Date(kb.updatedAt).toLocaleDateString()}
      </div>
    </div>
  );
};

const KnowledgeBaseSection = ({ onMessage, onError, onKbActivation }) => {
  const { t } = useTranslation();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentKB, setCurrentKB] = useState(null);
  const [kbName, setKbName] = useState(''); // New state for KB name
  const [kbText, setKbText] = useState(''); // New state for KB text
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [businessProfile, setBusinessProfile] = useState(null);

  const fetchBusinessProfile = useCallback(async () => {
    try {
      const response = await getBusinessProfile();
      //console.log('Fetched business profile in KB:', response);
      setBusinessProfile(response);
    } catch (err) {
      console.error('Error fetching business profile kb section:', err);
      setError(t('KnowledgeBaseSection.alerts.fetch_error'));
      if (onError) {
        onError(t('KnowledgeBaseSection.alerts.fetch_error'));
      }
    }
  }, [t, onError]);

  const fetchKnowledgeBases = useCallback(async () => {
    //console.log('Starting to fetch knowledge bases...');
    setLoading(true);
    try {
      const response = await getKnowledgeBaseDetails();
      //console.log('Fetched knowledge bases in KB:', response);

      const kbArray = Array.isArray(response) ? response : [];
      setKnowledgeBases(kbArray);
    } catch (err) {
      console.error('Error fetching knowledge bases:', err);
      setError(t('KnowledgeBaseSection.alerts.fetch_error'));
      if (onError) {
        onError(t('KnowledgeBaseSection.alerts.fetch_error'));
      }
      setKnowledgeBases([]);
    } finally {
      setLoading(false);
    }
  }, [t, onError]);

  useEffect(() => {
    //console.log('KnowledgeBaseSection mounted');
    fetchKnowledgeBases();
    fetchBusinessProfile();
  }, [fetchKnowledgeBases, fetchBusinessProfile]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  // **New handleKBAction function**
  const handleKBAction = useCallback(async (action, kb = null) => {

    switch (action) {
      case 'add':
        setCurrentKB(null);
        setKbName('');
        setKbText('');
        setShowModal(true);
        
        if (businessProfile?.sector) {
          try {
            // **Fetch the default template for the selected sector**
            const response = await getSectorTemplates(businessProfile.sector);
            const templates = response;
            if (templates.length > 0) {
              setKbText(templates[0].template); // Assuming only one template per sector
            } else {
              setError(t('KnowledgeBaseSection.alerts.no_template_found'));
              if (onError) {
                onError(t('KnowledgeBaseSection.alerts.no_template_found'));
              }
            }
          } catch (err) {
            console.error('Error fetching default KB template:', err);
            setError(t('KnowledgeBaseSection.alerts.template_fetch_error'));
            if (onError) {
              onError(t('KnowledgeBaseSection.alerts.template_fetch_error'));
            }
          }
        } else {
          setError(t('KnowledgeBaseSection.alerts.no_sector_selected'));
          if (onError) {
            onError(t('KnowledgeBaseSection.alerts.no_sector_selected'));
          }
        }
        break;

      case 'edit':
        setCurrentKB(kb);
        setKbName(kb.name);
        setKbText(kb.text);
        setShowModal(true);
        break;

      case 'delete':
        if (window.confirm(t('KnowledgeBaseSection.alerts.delete_confirm'))) {
          try {
            await deleteKnowledgeBase(kb._id);
            setMessage(t('KnowledgeBaseSection.alerts.delete_success'));
            if (onMessage) {
              onMessage(t('KnowledgeBaseSection.alerts.delete_success'));
            }
            fetchKnowledgeBases();
          } catch (err) {
            console.error('Error deleting knowledge base:', err);
            setError(t('KnowledgeBaseSection.alerts.delete_error'));
            if (onError) {
              onError(t('KnowledgeBaseSection.alerts.delete_error'));
            }
          }
        }
        break;

        case 'activate':
          if (kb.isActive) {
            onMessage(t('KnowledgeBaseSection.alerts.already_active'));
            return;
          }
          try {
            setLoading(true);
            // Deactivate all other active KBs
            const activeKBs = knowledgeBases.filter(existingKB => existingKB.isActive && existingKB._id !== kb._id);
            await Promise.all(activeKBs.map(existingKB => activateKnowledgeBase(existingKB._id, false)));
            // Activate the selected KB
            await activateKnowledgeBase(kb._id, true);
            setMessage(t('KnowledgeBaseSection.alerts.activate_success'));
            if (onMessage) {
              onMessage(t('KnowledgeBaseSection.alerts.activate_success'));
            }
            // Add this line to notify parent component
            if (onKbActivation) {
              onKbActivation();
            }
            fetchKnowledgeBases();
          } catch (err) {
            console.error('Error activating knowledge base:', err);
            setError(t('KnowledgeBaseSection.alerts.activate_error'));
            if (onError) {
              onError(t('KnowledgeBaseSection.alerts.activate_error'));
            }
          } finally {
            setLoading(false);
          }
          break;
      default:
        console.warn('Unhandled action:', action);
        break;
    }
  }, [businessProfile, fetchKnowledgeBases, knowledgeBases, t, onError, onMessage, onKbActivation]);

  const handleActivate = useCallback(async (kb) => {
    handleKBAction('activate', kb);
  }, [handleKBAction]);

  const handleEdit = useCallback(async (kb) => {
    handleKBAction('edit', kb);
  }, [handleKBAction]);

  const handleDelete = useCallback(async (kb) => {
    handleKBAction('delete', kb);
  }, [handleKBAction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessProfile?._id) {
      setError(t('KnowledgeBaseSection.alerts.no_business_profile_error'));
      if (onError) {
        onError(t('KnowledgeBaseSection.alerts.no_business_profile_error'));
      }
      return;
    }

    const data = {
      name: kbName,
      text: kbText,
    };

    try {
      if (currentKB) {
        await updateKnowledgeBase(currentKB._id, data.name, data.text);
        setMessage(t('KnowledgeBaseSection.alerts.success_message'));
        if (onMessage) {
          //onMessage(t('KnowledgeBaseSection.alerts.success_message'));
        }
      } else {
        await createKnowledgeBase(
          data.name, 
          data.text, 
          businessProfile._id
        );
        setMessage(t('KnowledgeBaseSection.alerts.create_success'));
        if (onMessage) {
          onMessage(t('KnowledgeBaseSection.alerts.create_success'));
        }
      }
      setShowModal(false);
      setKbName('');
      setKbText('');
      fetchKnowledgeBases();
    } catch (err) {
      console.error('Error saving knowledge base:', err);
      setError(t('KnowledgeBaseSection.alerts.save_error'));
      if (onError) {
        onError(t('KnowledgeBaseSection.alerts.save_error'));
      }
    }
  };

  const filteredKBs = knowledgeBases.filter(kb => 
    kb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kb.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div>
        <h2 className={`${colors.text.heading} text-2xl font-semibold mb-2`}>
          {t('KnowledgeBaseSection.title')}
        </h2>
        <p className={`${colors.text.body}`}>
          {t('KnowledgeBaseSection.description')}
        </p>
      </div>

      {/* Alert Messages */}
      {(message || error) && (
        <div className={`p-4 rounded-xl ${
          message ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        }`}>
          <div className="flex items-center">
            {message ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <p>{message || error}</p>
          </div>
        </div>
      )}

      {/* Control Panel */}
      <div className="flex justify-end space-x-4">
        <GradientButton
          onClick={() => handleKBAction('add')}
          className="flex items-center"
          disabled={loading}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('KnowledgeBaseSection.buttons.add')}
        </GradientButton>
      </div>

      {/* Knowledge Bases Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredKBs.map((kb) => (
          <KnowledgeBaseCard
            key={kb._id}
            kb={kb}
            onEdit={() => handleEdit(kb)}
            onDelete={() => handleDelete(kb)}
            onActivate={() => handleActivate(kb)}
          />
        ))}
      </div>

      {/* KB Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Container className="w-full max-w-3xl bg-white p-6 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className={`${colors.text.heading} text-xl`}>
                {currentKB ? t('KnowledgeBaseSection.modal.title_edit') : t('KnowledgeBaseSection.modal.title_add')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Success and Error Alerts */}
              {message && (
                <div className="p-4 rounded-xl bg-emerald-50 text-emerald-700">
                  <div className="flex items-center">
                    <Check className="w-5 h-5 mr-2" />
                    <p>{message}</p>
                  </div>
                </div>
              )}
              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-700">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    <p>{error}</p>
                  </div>
                </div>
              )}

              {/* KB Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('KnowledgeBaseSection.modal.name_label')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={kbName}
                  onChange={(e) => setKbName(e.target.value)}
                  className="w-full rounded-lg border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                  required
                  placeholder={t('KnowledgeBaseSection.modal.name_placeholder')}
                />
              </div>

              {/* KB Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('KnowledgeBaseSection.modal.content_label')}
                </label>
                <textarea
                  name="text"
                  rows="10"
                  value={kbText}
                  onChange={(e) => setKbText(e.target.value)}
                  className="w-full rounded-lg border-gray-200 focus:ring-violet-500 focus:border-violet-500"
                  required
                  placeholder={t('KnowledgeBaseSection.modal.content_placeholder')}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  {t('KnowledgeBaseSection.buttons.cancel')}
                </button>
                <GradientButton type="submit" disabled={loading}>
                  {currentKB ? t('KnowledgeBaseSection.buttons.update') : t('KnowledgeBaseSection.buttons.create')}
                </GradientButton>
              </div>
            </form>
          </Container>
        </div>
      )}

      {/* Empty State */}
      {knowledgeBases.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('KnowledgeBaseSection.empty_state.title')}</h3>
          <p className="text-gray-500 mb-4">
            {t('KnowledgeBaseSection.empty_state.description')}
          </p>
          <GradientButton
            onClick={() => handleKBAction('add')}
            className="inline-flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('KnowledgeBaseSection.empty_state.add_button')}
          </GradientButton>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseSection;
